import * as React from 'react';
import { useCallback } from 'react';

import { DialogRoute } from 'bernie-core';
import { Layer, Viewport, ViewSmall } from 'bernie-view';

import { UitkFullscreenDialog, UitkDialogContent } from 'uitk-react-dialog';
import { UitkToolbar, ToolbarType } from 'uitk-react-toolbar';
import { liveAnnounce, Level } from 'uitk-react-utils';

import { useStores } from 'utility/hooks';
import { trackAnalytics } from 'utility';
import { FlightAction, FlightsStandardOffer } from '__generated__/api';

import { DetailsAndFares } from '.';

interface DetailsAndFaresDialogProps {
  onDialogClose?: () => void;
  openForcedChoiceModal?: boolean;
  handleOfferSelect?: (selectOfferAction: FlightAction.Fragment) => void;
  showSummaryAndDetails: boolean;
  detailsAndFaresDialogId?: string;
  itemIndex?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  availableFaresInformation?: any;
  hygieneAmenitiesPresentation?: FlightsStandardOffer.HygieneAmenitiesPresentation;
}

export const DetailsAndFaresDialog = ({
  onDialogClose,
  openForcedChoiceModal,
  handleOfferSelect,
  showSummaryAndDetails,
  detailsAndFaresDialogId,
  itemIndex,
  availableFaresInformation,
  hygieneAmenitiesPresentation
}: DetailsAndFaresDialogProps) => {
  const {
    routeStore,
    dynamicElementsStore,
    priceSummaryStore,
    flightsSelectedJourneyReviewStore,
    analytics: analyticsStore
  } = useStores();
  const {
    fareChoiceInformation,
    dismissDetailedJourneyInformation: { icon },
    openAccessibility
  } = availableFaresInformation;
  const fareTypes = fareChoiceInformation ? fareChoiceInformation.fareTypes : null;
  const initialFareTypeSelectedIndex = fareTypes ? fareTypes.findIndex(fare => fare.selected) : 0;
  const iconLabel = icon.description;

  const setSelected = useCallback(
    (index: number) => {
      if (fareTypes) {
        const selectedFareType = fareTypes.find(fareType => fareType.selected);
        if (selectedFareType) {
          selectedFareType.selected = false;
        }
        fareTypes[index].selected = true;
      }
    },
    [fareTypes]
  );

  const trackCloseAnalytics = () => {
    const {
      dismissDetailedJourneyInformation: { analytics }
    } = availableFaresInformation;
    trackAnalytics(analytics, analyticsStore);
  };

  const updateFlightInformation = (identifier: string) => {
    flightsSelectedJourneyReviewStore.updateInformation(
      itemIndex,
      identifier,
      fareTypes,
      dynamicElementsStore,
      priceSummaryStore,
      routeStore
    );
  };

  const onApplySelectDialog = (identifier: string) => {
    updateFlightInformation(identifier);
  };

  const onApplySelectWithFareSelectionDialog = (identifier: string) => {
    fareTypes.find(fareType => fareType.selected).selected = false;
    fareTypes.find(fareType => fareType.identifier == identifier).selected = true;
    onApplySelectDialog(identifier);
  };

  const closeDialog = () => {
    setSelected(initialFareTypeSelectedIndex);
    trackCloseAnalytics();
    routeStore.closeDialog('pwaDialog');
    onDialogClose && onDialogClose();
  };

  const announceDialogOpen = () => {
    setTimeout(() => {
      liveAnnounce(openAccessibility, Level.POLITE);
    }, 500);
  };

  return (
    <Viewport>
      <ViewSmall>
        {detailsAndFaresDialogId && (
          <div data-test-id="listing-details-and-fares">
            <DialogRoute id={detailsAndFaresDialogId}>
              <Layer id={`fullscreenDialog-${detailsAndFaresDialogId}`} onDismiss={closeDialog}>
                <UitkFullscreenDialog
                  dialogShow={true}
                  openCallback={announceDialogOpen}
                  className="custom-dialog"
                  strictFullscreen={true}
                >
                  <UitkToolbar action={closeDialog} iconLabel={iconLabel} key="UitkToolbar" type={ToolbarType.CLOSE} />
                  <UitkDialogContent key="UitkDialogContent-1" zeroPadding={true}>
                    <DetailsAndFares
                      hygieneAmenitiesPresentation={hygieneAmenitiesPresentation}
                      showSummaryAndDetails={showSummaryAndDetails}
                      itemIndex={itemIndex}
                      availableFaresInformation={availableFaresInformation}
                      setSelected={setSelected}
                      useOwnFooter={true}
                      openForcedChoiceModal={openForcedChoiceModal}
                      onApplySelect={onApplySelectWithFareSelectionDialog}
                      handleOfferSelect={handleOfferSelect}
                      isSmallViewport={true}
                    />
                  </UitkDialogContent>
                </UitkFullscreenDialog>
              </Layer>
            </DialogRoute>
          </div>
        )}
      </ViewSmall>
    </Viewport>
  );
};
